import axios from "axios";
import store from "./components/store/store";
import { loginFailure } from "./components/store/auth/authSlice";
import { API_BASE_URL, NODE_API_BASE_URL } from "./components/config";
import {
  COOKIE_SESSION, authHeaders, setSession, clearSession,
  refreshPayload, canRefresh,
} from "./components/cms/utils/session";

/**
 * Access tokens expire every fifteen minutes, so this is where a session is
 * actually kept alive.
 *
 * The rule the server gives us is narrow on purpose: only a 401 carrying
 * `code: "token_expired"` means "this token aged out, get another one". A
 * `token_invalid` is a token that was never good, and a 401 from the refresh
 * endpoint itself means the seven days are up — both end the session rather than
 * trying again with something that cannot work.
 *
 * Refreshing is single-flight. The refresh token rotates on every use, so two
 * refreshes racing would each invalidate the other's token and log out a user
 * who did nothing wrong. Every request that hits an expired token therefore
 * waits on the same in-flight refresh and retries with whatever it produces.
 */

/* Its own instance: the refresh call must never pass through the interceptor
   that triggers refreshes, or a failing refresh would try to refresh itself. */
const plain = axios.create();

let isLoggingOut = false;

function forceLogout() {
  if (isLoggingOut) return;
  // HashRouter: real path is in window.location.hash, not pathname
  if (window.location.hash.includes("/cms/login")) return;
  isLoggingOut = true;
  // Save the current hash route so Login can redirect back to it after re-auth
  const currentPath = window.location.hash.replace(/^#/, "").trim();
  if (currentPath && currentPath !== "/" && !currentPath.includes("/cms/login")) {
    sessionStorage.setItem("postLoginRedirect", currentPath);
  }
  /* The marker cookie goes too — `localStorage.clear()` cannot touch it, and a
     marker left behind would tell the next page load it is still signed in. */
  clearSession();
  localStorage.clear();
  store.dispatch(loginFailure());
  window.location.replace("/#/cms/login");
}

/**
 * Endpoints that answer business failures with HTTP 401 instead of 4xx.
 *
 * e.g. subscription/update-user-subscription returns
 *   401 { success: false, message: "No active subscription found for this user." }
 * That is not an auth failure — logging the user out here would clear their
 * session and bounce them to login mid-task. Such a response is passed through
 * so the calling screen can show the API's message instead.
 */
const BUSINESS_401_ENDPOINTS = ["subscription/update-user-subscription"];

const isBusiness401 = (error) => {
  const url = error?.config?.url || "";
  if (!BUSINESS_401_ENDPOINTS.some((path) => url.includes(path))) return false;
  // Only treat it as a business error when the body actually explains itself;
  // a bare 401 (expired token) still means log out.
  const data = error?.response?.data;
  return !!(data && (data.message || data.error));
};

const REFRESH_PATH = "cms-refresh-token";

/* Calls that are how a session begins or ends. A 401 from one of these is the
   answer, not an expired token — refreshing in response to a rejected login only
   turns "wrong password" into a confusing "No refresh token". */
const SESSION_PATHS = ["cms-internal-login", "cms-logout", REFRESH_PATH];
const isSessionCall = (url = "") => SESSION_PATHS.some((p) => String(url).includes(p));

/* Only our own API gets the session token. The elastic search host and the
   call-centre API are separate services; sending them an Authorization header
   would at best be ignored and at worst fail their CORS preflight. */
const OUR_HOSTS = [API_BASE_URL, NODE_API_BASE_URL].filter(Boolean);
const isOurApi = (url = "") => OUR_HOSTS.some((base) => String(url).startsWith(base));

// ── Refresh, at most one at a time ──────────────────────────────────────────
let refreshPromise = null;

/**
 * Whether a failure means the session is genuinely over.
 *
 * Only the server can say that, and it says it with a 401 or 403. Anything that
 * never got a response — offline, a dropped connection, a CORS preflight the API
 * has not been told to allow on the refresh route — says nothing about the
 * session, and treating it as "logged out" ends a seven-day session over a
 * momentary blip. Same for a retry that comes back 500: the token was fine, the
 * endpoint was not.
 *
 * Locally-thrown failures that really do mean "unusable session" set the flag
 * themselves, since they have no response to read.
 */
const sessionOver = (err) => {
  if (err?.sessionOver) return true;
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

const fatal = (message) => Object.assign(new Error(message), { sessionOver: true });

const requestNewTokens = async () => {
  if (!canRefresh()) throw fatal("Nothing to refresh with");

  /* The body is empty in cookie mode — the refresh token is a cookie the browser
     attaches itself, which is also why this call needs credentials. */
  const res = await plain.post(`${API_BASE_URL}/${REFRESH_PATH}`, refreshPayload(), {
    withCredentials: true,
  });
  /* The server has already invalidated the token we sent, so a response we
     cannot store leaves the session unusable — better to fail here and log out
     cleanly than to fail on every request after this one. */
  if (!setSession(res.data || {})) throw fatal("Refresh response was incomplete");
};

/** Everyone waiting on an expired token shares one refresh. */
const refreshOnce = () => {
  if (!refreshPromise) {
    refreshPromise = requestNewTokens().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

/**
 * Re-issue a request that failed on an expired token.
 *
 * The Authorization header is rewritten rather than merely added: nearly every
 * call site builds its own header from storage, so the config being retried
 * still carries the token that just expired.
 */
const retryWithFreshToken = async (config) => {
  await refreshOnce();
  return axios({
    ...config,
    headers: { ...(config.headers || {}), ...authHeaders() },
    __refreshed: true,
  });
};

const codeOf = (body) => (body && typeof body === "object" ? body.code : undefined);

/**
 * Give an axios instance the session behaviour.
 *
 * Instances made with `axios.create()` do not inherit anything registered on
 * the default export, so one created elsewhere would send its requests without
 * the session and, worse, never refresh on a 401 — it would just fail, while
 * every other call on the screen recovered. Anything that creates its own
 * instance has to be handed this.
 */
export function installAuthInterceptors(instance) {
  /* Cookies only travel cross-origin when the request asks for them, and the CMS
     is served from a different host to its API. Switched on with the mode rather
     than always, because sending credentials to an API that does not expect them
     makes its CORS policy stricter for no benefit. */
  if (COOKIE_SESSION) instance.defaults.withCredentials = true;

  /* Saves every new call site from rebuilding the same header — and means a
     retried request cannot be sent with a stale one. Anything that set its own
     header is left alone. */
  instance.interceptors.request.use((config) => {
    const url = config.url || "";
    if (url.includes(REFRESH_PATH)) return config;
    if (!isOurApi(url)) return config;
    const already = config.headers?.Authorization || config.headers?.authorization;
    if (already) return config;
    config.headers = { ...(config.headers || {}), ...authHeaders() };
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Some endpoints return HTTP 200 with { status: 401 } in the body
      if (response?.data?.status === 401) {
        const config = response.config || {};
        // Same rule as a real 401: one refresh attempt unless the token is void.
        if (codeOf(response.data) !== "token_invalid" && !config.__refreshed) {
          return retryWithFreshToken(config).catch((err) => {
            if (sessionOver(err)) forceLogout();
            throw err;
          });
        }
        forceLogout();
      }
      return response;
    },
    (error) => {
      const config = error?.config;
      if (error?.response?.status !== 401 || !config) return Promise.reject(error);
      if (isBusiness401(error)) return Promise.reject(error);

      /* A 401 from login, logout or refresh is the endpoint's own answer. Login
         rejects bad credentials and the screen shows that message; refresh
         rejecting means the seven days are up. Neither is retryable, and only
         the ones that ended a live session end it here — a failed login never
         had one to end. */
      if (isSessionCall(config.url)) {
        if (!String(config.url || "").includes("cms-internal-login")) forceLogout();
        return Promise.reject(error);
      }

      /* `token_invalid` is a malformed or revoked token, not an aged one —
         refreshing would answer a question nobody asked. Straight to login. */
      if (codeOf(error.response?.data) === "token_invalid") {
        forceLogout();
        return Promise.reject(error);
      }

      /* Every other 401 gets one refresh attempt, not just the ones labelled
         `token_expired`. A 401 whose body carries no code at all is the common
         case — a gateway timing out the token, an endpoint that answers before
         reaching the code that would label it — and treating an unlabelled 401
         as "log out immediately" ends a session that one refresh would have
         saved. If the session really is over, the refresh call says so and the
         logout happens a round trip later, which costs nothing. */
      if (!config.__refreshed) {
        return retryWithFreshToken(config).catch((err) => {
          if (sessionOver(err)) forceLogout();
          throw err;
        });
      }

      /* Already retried once with a fresh token and still 401 — refreshing again
         would loop. */
      forceLogout();
      return Promise.reject(error);
    },
  );
}


/** Install them on the default axios instance, which most of the app uses. */
export function setupAxiosInterceptors() {
  installAuthInterceptors(axios);
}

/**
 * End the session on the server as well as here.
 *
 * Without this the refresh token stays valid for its full seven days — logging
 * out on a shared machine would leave a working key behind.
 */
export const revokeSession = async () => {
  /* Always attempted in cookie mode: only the server can expire an httpOnly
     cookie, so skipping the call would leave a working session behind even
     though this browser has forgotten about it. */
  if (!COOKIE_SESSION && !canRefresh()) return;
  try {
    await plain.post(`${API_BASE_URL}/cms-logout`, refreshPayload(), {
      withCredentials: true,
    });
  } catch {
    // The local session is being cleared regardless; a failed revoke must not
    // trap someone on a page they are trying to leave.
  } finally {
    clearSession();
  }
};
